import React, { useState, useEffect } from 'react';
import { GripVertical, Star, Save, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../../services/api';

const FeaturedOrderManager = ({ onClose }) => {
  const [featuredContent, setFeaturedContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadFeaturedContent();
  }, []);

  const loadFeaturedContent = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/featured-content');
      // Sort by featured_order, then by title for items without order
      const sorted = response.data.sort((a, b) => {
        const orderA = a.featured_order ?? 999;
        const orderB = b.featured_order ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return a.title.localeCompare(b.title);
      });
      setFeaturedContent(sorted);
    } catch (error) {
      console.error('Error loading featured content:', error);
    } finally {
      setLoading(false);
    }
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= featuredContent.length) return;

    const newOrder = [...featuredContent];
    const [removed] = newOrder.splice(index, 1);
    newOrder.splice(newIndex, 0, removed);
    setFeaturedContent(newOrder);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const contentIds = featuredContent.map(item => item.id);
      await api.put('/admin/featured-content/reorder', { content_ids: contentIds });
      setHasChanges(false);
      alert('Featured order saved successfully!');
    } catch (error) {
      console.error('Error saving featured order:', error);
      alert('Failed to save featured order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-surface border border-white/20 rounded-2xl p-8">
          <Loader2 className="h-8 w-8 animate-spin text-electric mx-auto" />
          <p className="text-muted-foreground mt-4">Loading featured content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-white/20 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Star className="h-6 w-6 text-electric" />
            <h2 className="text-xl font-bold text-foreground">Featured Content Order</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Instructions */}
        <div className="px-6 py-3 bg-white/5 border-b border-white/10">
          <p className="text-sm text-muted-foreground">
            Use the arrows to reorder featured content. The order here determines how content appears in the homepage hero section.
          </p>
        </div>

        {/* Content List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {featuredContent.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No featured content found</p>
          ) : (
            featuredContent.map((item, index) => (
              <div
                key={item.id}
                className="flex items-center space-x-3 p-3 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 transition-colors"
              >
                {/* Order Number */}
                <div className="w-8 h-8 flex items-center justify-center bg-electric/20 text-electric rounded-lg font-bold text-sm">
                  {index + 1}
                </div>

                {/* Thumbnail */}
                <img
                  src={item.thumbnail}
                  alt={item.title}
                  className="w-16 h-10 object-cover rounded-lg"
                />

                {/* Title & Type */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-foreground font-medium truncate">{item.title}</h4>
                  <p className="text-xs text-muted-foreground capitalize">
                    {item.content_type?.replace('_', ' ')}
                  </p>
                </div>

                {/* Move Buttons */}
                <div className="flex flex-col space-y-1">
                  <button
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                    className={`p-1.5 rounded-lg transition-colors ${
                      index === 0
                        ? 'text-muted-foreground/30 cursor-not-allowed'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/10'
                    }`}
                    title="Move up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === featuredContent.length - 1}
                    className={`p-1.5 rounded-lg transition-colors ${
                      index === featuredContent.length - 1
                        ? 'text-muted-foreground/30 cursor-not-allowed'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/10'
                    }`}
                    title="Move down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {featuredContent.length} featured item{featuredContent.length !== 1 ? 's' : ''}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                hasChanges && !saving
                  ? 'bg-electric text-background hover:bg-electric/90'
                  : 'bg-white/10 text-muted-foreground cursor-not-allowed'
              }`}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              <span>{saving ? 'Saving...' : 'Save Order'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedOrderManager;
